import React from 'react';
import { useCommunityStats, useIsMobile } from '../../hooks/useReaderCorner';

const C = { rust: '#B85C38', forest: '#2D4A3E', cream: '#F5F0E8', gold: '#d4a84b', grey: '#8B7355', ink: '#292524', purple: '#7c3aed' };

function fmtPages(n) {
  if (!n) return '—';
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(n % 1_000_000 === 0 ? 0 : 1)}M`;
  if (n >= 1_000) return `${Math.round(n / 1000)}k`;
  return String(n);
}

function StatCard({ emoji, value, label, valueColor }) {
  return (
    <div style={{ background: '#fffdf8', border: `1px solid ${C.gold}55`, borderRadius: 'var(--radius-xl)', padding: '20px 18px', textAlign: 'center', boxShadow: '0 6px 22px rgba(45,74,62,0.06)' }}>
      <p style={{ fontSize: 22, margin: '0 0 10px' }}>{emoji}</p>
      <p style={{ fontSize: 30, fontWeight: 800, color: valueColor || C.ink, margin: '0 0 8px', fontFamily: 'var(--font-display)' }}>{value}</p>
      <p style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase', color: C.grey, margin: 'var(--space-none)' }}>{label}</p>
    </div>
  );
}

export default function StatsTab() {
  const { stats, loading } = useCommunityStats();
  const isMobile = useIsMobile();

  if (loading) {
    return <p style={{ textAlign: 'center', color: C.grey, fontSize: 'var(--font-size-base)' }}>Loading…</p>;
  }

  if (!stats) {
    return (
      <p style={{ textAlign: 'center', color: C.grey, fontSize: 'var(--font-size-base)', fontStyle: 'italic' }}>
        No community stats yet, check back soon.
      </p>
    );
  }

  if (!stats.sufficient_data) {
    return (
      <div style={{ textAlign: 'center', padding: '40px 20px' }}>
        <p style={{ fontSize: 'var(--font-size-h3)', margin: '0 0 10px' }}>🌱</p>
        <p style={{ fontSize: 'var(--font-size-md)', fontWeight: 700, color: C.forest, margin: '0 0 6px' }}>Not enough readers yet</p>
        <p style={{ fontSize: 12.5, color: C.grey, margin: 'var(--space-none)', maxWidth: 340, marginLeft: 'auto', marginRight: 'auto', lineHeight: 1.5 }}>
          Community stats stay private until at least 25 readers are contributing,
          right now we're not there yet.
        </p>
      </div>
    );
  }

  const maxTropeCount = Math.max(...(stats.top_tropes || []).map(t => t.count), 1);
  const TROPE_COLORS = [C.rust, C.gold, C.forest, C.gold, C.forest];

  return (
    <div>
      <p style={{ textAlign: 'center', fontSize: isMobile ? 9.5 : 10.5, fontWeight: 700, letterSpacing: isMobile ? 1 : 2, textTransform: 'uppercase', color: C.gold, margin: '0 0 20px', padding: isMobile ? '0 8px' : 0 }}>
        The community's reading year · pulled from readers' profile stats
      </p>

      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr 1fr' : 'repeat(3, 1fr)', gap: isMobile ? 10 : 16, marginBottom: 'var(--space-lg)' }}>
        <StatCard emoji="📚" value={stats.avg_books_read_year != null ? Math.round(stats.avg_books_read_year) : '—'} label="Avg books read this year" />
        <StatCard emoji="⭐" value={stats.avg_rating != null ? Number(stats.avg_rating).toFixed(1) : '—'} label="Avg rating across the community" valueColor={C.gold} />
        <StatCard emoji="📖" value={fmtPages(stats.pages_read_year)} label="Pages read together this year" valueColor={C.rust} />
        <StatCard emoji="✍️" value={(stats.authors_on_shelves || 0).toLocaleString()} label="Authors on community shelves" valueColor={C.purple} />
        <StatCard emoji="🎯" value={stats.goals_on_track_pct != null ? `${Math.round(stats.goals_on_track_pct)}%` : '—'} label="Of reading goals on track" valueColor={C.forest} />
        <StatCard emoji="💎" value={stats.gem_title || 'None yet'} label="Highest-rated gem this week" valueColor={C.rust} />
      </div>

      {stats.top_tropes?.length > 0 && (
        <div style={{ background: '#fffdf8', border: `1px solid ${C.gold}55`, borderRadius: 'var(--radius-xl)', padding: isMobile ? '18px 16px' : '20px 24px', maxWidth: 620, margin: '0 auto' }}>
          <p style={{ textAlign: 'center', fontSize: 15, fontWeight: 800, color: C.ink, margin: '0 0 3px' }}>Top tropes this month</p>
          <p style={{ textAlign: 'center', fontSize: 11.5, color: C.grey, margin: '0 0 16px' }}>from readers' Top Tropes blocks</p>
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            {stats.top_tropes.map((t, i) => (
              <div key={t.name} style={{ display: 'flex', alignItems: 'center', gap: isMobile ? 8 : 14, padding: '10px 0', borderTop: i > 0 ? '1px solid #eee' : 'none' }}>
                <span style={{ fontSize: isMobile ? 11 : 12.5, fontWeight: 700, color: C.ink, width: isMobile ? 92 : 140, flexShrink: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t.name}</span>
                <div style={{ flex: 1, height: 10, background: '#eee6d8', borderRadius: 5, overflow: 'hidden' }}>
                  <div style={{ height: '100%', width: `${(t.count / maxTropeCount) * 100}%`, background: TROPE_COLORS[i % TROPE_COLORS.length], borderRadius: 5 }} />
                </div>
                <span style={{ fontSize: isMobile ? 10.5 : 12, color: C.grey, width: isMobile ? 38 : 50, textAlign: 'right', flexShrink: 0 }}>{t.count.toLocaleString()}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <p style={{ textAlign: 'center', fontSize: 'var(--font-size-xxs)', color: '#c4b8a0', margin: '18px 0 0' }}>
        Last updated {new Date(stats.computed_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}
      </p>
    </div>
  );
}